import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Disclosures = () => {
  const tableOfContents = [
    { id: 'compensation', title: 'Compensation Disclosure' },
    { id: 'carrier-independence', title: 'Carrier Independence' },
    { id: 'trademarks', title: 'Trademarks' },
    { id: 'marketing', title: 'Marketing Disclosures' },
    { id: 'fair-marketing', title: 'Fair Marketing Practices' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents - Sticky on desktop */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-lg font-semibold text-foreground mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.title}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold text-foreground mb-4">Disclosures</h1>
              <p className="text-muted-foreground mb-8">Last updated: January 2024</p>
              
              <div className="prose prose-lg max-w-none space-y-8">
                <div className="text-muted-foreground leading-relaxed">
                  <p className="mb-6">
                    The following disclosures provide important information about Safurra's business practices, 
                    compensation arrangements, and relationships with insurance carriers and partners.
                  </p>
                </div>

                <section id="compensation">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Compensation Disclosure</h2>
                  <div className="text-muted-foreground space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
                      <p className="text-yellow-800">
                        <strong>Important:</strong> Safurra may receive compensation from insurance carriers, partners, 
                        and service providers when you purchase a policy or complete certain actions through our platform.
                      </p>
                    </div>
                    <p>
                      <strong className="text-foreground">Commission Structure:</strong> We may earn commissions from insurance carriers 
                      when you purchase a policy. These commissions are typically paid by the carrier and do not increase your premiums.
                    </p>
                    <p>
                      <strong className="text-foreground">Referral Fees:</strong> We may receive referral fees from partners 
                      for connecting you with their services, including carriers, financial advisors, or other service providers.
                    </p>
                    <p>
                      <strong className="text-foreground">Advertising Revenue:</strong> Some insurance carriers or partners may pay 
                      advertising fees for prominent placement on our platform.
                    </p>
                    <p>
                      This compensation may influence the placement or prominence of certain products, carriers, or offers on our platform, 
                      but it does not affect the substance of our recommendations or the accuracy of quote information.
                    </p>
                  </div>
                </section>

                <section id="carrier-independence">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Carrier Independence</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      <strong className="text-foreground">Not All Carriers Displayed:</strong> Safurra does not display quotes 
                      or products from every insurance carrier in the market. Our platform includes carriers with whom we have 
                      established business relationships.
                    </p>
                    <p>
                      <strong className="text-foreground">Quote Estimates:</strong> Quotes provided through our platform are estimates 
                      based on the information you provide. Final rates, terms, and coverage are subject to the carrier's underwriting 
                      process and approval.
                    </p>
                    <p>
                      <strong className="text-foreground">Independent Comparison:</strong> We encourage you to compare options from 
                      multiple sources and consult with other agents or directly with carriers to ensure you find the best coverage 
                      for your needs.
                    </p>
                    <div className="bg-muted p-6 rounded-lg">
                      <p className="text-sm">
                        <strong className="text-foreground">Carrier Selection Criteria:</strong> We select carrier partners based on 
                        financial strength ratings, product quality, customer service reputation, and competitive pricing.
                      </p>
                    </div>
                  </div>
                </section>

                <section id="trademarks">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Trademarks</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      All trademarks, logos, brand names, and company names referenced on our website belong to their respective owners. 
                      The use of these marks does not imply endorsement by or affiliation with the trademark owners, unless explicitly stated.
                    </p>
                    <p>
                      Safurra and the Safurra logo are trademarks of Safurra Insurance Services, LLC. All rights reserved.
                    </p>
                  </div>
                </section>

                <section id="marketing">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Marketing Disclosures</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      <strong className="text-foreground">Testimonials and Reviews:</strong> Customer testimonials and reviews 
                      featured on our website represent individual experiences and may not be typical results. We may provide 
                      incentives for reviews, which will be clearly disclosed.
                    </p>
                    <p>
                      <strong className="text-foreground">Marketing Communications:</strong> We may send marketing communications 
                      about insurance products, services, and educational content. You can opt out of marketing communications 
                      at any time through the unsubscribe link or by contacting us.
                    </p>
                    <p>
                      <strong className="text-foreground">Third-Party Marketing:</strong> We may allow select partners to market 
                      relevant financial products and services to our users. You can opt out of third-party marketing communications.
                    </p>
                  </div>
                </section>

                <section id="fair-marketing">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Fair Marketing Practices</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      <strong className="text-foreground">No Guarantees:</strong> We do not guarantee that you will qualify for 
                      coverage or receive the quoted rates. Insurance approval and final pricing depend on the carrier's underwriting 
                      and your specific circumstances.
                    </p>
                    <p>
                      <strong className="text-foreground">Savings Claims:</strong> Any potential savings amounts shown are estimates 
                      based on publicly available rate information and may not reflect your actual savings.
                    </p>
                    <p>
                      <strong className="text-foreground">Educational Content:</strong> Information provided on our website is for 
                      educational purposes and should not be considered personalized insurance advice. Always consult with a 
                      qualified insurance professional for advice specific to your situation.
                    </p>
                    <div className="bg-muted p-6 rounded-lg">
                      <p>
                        <strong className="text-foreground">Questions or Concerns:</strong> If you have questions about our 
                        disclosures or business practices, please contact us at{' '}
                        <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a>.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Disclosures;